import React from 'react'
// libs
import { useSnackbar } from 'notistack'
import { useSelector } from 'react-redux'
import { useMutation } from '@apollo/client/react'
// components
import { IconButton } from 'components/UI'
import LastUpdated, { LastUpdatedVariant } from 'components/UI/LastUpdated'
// icons
import RefreshIcon from '@mui/icons-material/Refresh'
// hooks
import { useChartsRefresh } from 'hooks/useChartsRefresh'
// graphql
import { CHART_DATA_QUERY } from 'graphql/chart/queries'
import { CHARTS_REFRESH_MUTATION } from 'graphql/chart/mutations'
// constants
import { CHART_REFRESH_LOCKED_MESSAGE } from 'constants/charts'
// types
import { DashboardItemType, IChartsRefreshInput } from 'types/graphql'
// store
import { currentDashboardSelector, isDashboardEditSelector } from 'store/reducers/dashboards'
// views
import { Wrapper } from './views'
// utils
import { uniq } from 'lodash'
import { handleError } from 'utils/errors/handlers'

// Recomputes every chart of the dashboard, sections included, but those refreshed in the last CHART_REFRESH_INTERVAL;
// each tile shows its loader until its notification
const RefreshChartsButton: React.FC = () => {
  const { enqueueSnackbar } = useSnackbar()

  const isEdit = useSelector(isDashboardEditSelector)
  const currentDashboard = useSelector(currentDashboardSelector)

  const chartIds = uniq(
    (currentDashboard?.widgets || [])
      .filter((widget) => widget.item.itemType === DashboardItemType.Chart)
      .map((widget) => widget.item.settings?.chartId)
      .filter(Boolean)
  )

  const { refreshableIds, computedAgo } = useChartsRefresh(chartIds)

  // The tiles on screen read REFRESHING right after
  const [refreshCharts, { loading }] = useMutation<unknown, { input: IChartsRefreshInput }>(CHARTS_REFRESH_MUTATION, {
    refetchQueries: [CHART_DATA_QUERY]
  })

  const onClick = async () => {
    try {
      await refreshCharts({ variables: { input: { chartIds: refreshableIds } } })

      enqueueSnackbar('Charts are refreshing. They update as soon as the new numbers are ready.', { variant: 'info' })
    } catch (error) {
      handleError(error, 'RefreshChartsButton.onClick')
    }
  }

  // While the dashboard is edited its tiles are being set up, not read
  if (isEdit || !chartIds.length) {
    return null
  }

  return (
    <Wrapper>
      {computedAgo && (
        <LastUpdated value={computedAgo} variant={LastUpdatedVariant.Dark} data-testid='charts-updated' />
      )}
      <IconButton
        preset='DARK_BLUE'
        data-testid='refresh-charts'
        description={refreshableIds.length ? 'Refresh all charts' : CHART_REFRESH_LOCKED_MESSAGE}
        disabled={loading || !refreshableIds.length}
        onClick={onClick}>
        <RefreshIcon />
      </IconButton>
    </Wrapper>
  )
}

export default RefreshChartsButton
